import React, { useCallback } from 'react';
import styled from 'styled-components';
import { AccordionStateProvider } from '@hotelplan/components.common.accordion';
import { WhiteBoxModal } from '@hotelplan/components.common.modals';
import {
  FdrCheckoutNoResults,
  FdrCheckoutResults,
  FdrCheckoutWaitingScreen,
} from '@hotelplan/fdr.regular.fusion.fdr-checkout';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { FdrLinkType, FdrWaitingScreenType } from '@hotelplan/supergraph-api';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import WishlistListCompareViewContentSkeleton from 'components/domain/wishlist/WishlistListCompareViewContentSkeleton.skeleton';
import { useWishlistSelectedItemsContext } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import { normalizeFdrCheckoutComponent } from 'fdr/components/local/fdr-checkout/fdr-checkout.mappers';
import { useFdrPageFooter } from 'fdr/components/local/fdr-footer';
import { FdrFooterPaymentMethods } from 'fdr/components/local/fdr-footer-payment-methods';
import { IFdrCheckoutOffer } from 'fdr/components/local/fdr-orl/fdr-orl.types';
import useFdrOrlCheckout from 'fdr/components/local/fdr-orl/useFdrOrlCheckout';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';
import { useFdrWaitingScreenQuery } from 'fdr/schemas/query/waiting-screen/fdr-waiting-screen.generated';
import FdrWishlistCompareItemsDesktop from './fdr-wishlist-compare-items-desktop';
import FdrWishlistCompareItemsMobile from './fdr-wishlist-compare-items-mobile';

const CheckoutWaitingScreenModal = styled(WhiteBoxModal).attrs({
  name: 'checkoutWaitingScreen',
  title: '',
})(
  sx2CssThemeFn({
    '.modal-wrapper': {
      variant: 'modals.actionlessModalWrapper',
    },
    '.modal-header': {
      borderBottom: '0',
      marginTop: [3, 5],
      position: ['relative', 'absolute'],
      right: [3, 5],
      padding: '0',
    },
    '.modal-body': {
      pt: [2, '32px'],
      pb: [3, '32px'],
    },
  })
);

const CheckoutResultsModal = styled(WhiteBoxModal).attrs({
  name: 'checkoutResultsModal',
  title: '',
})(
  sx2CssThemeFn({
    '.modal-header': {
      display: 'none',
    },
    '.modal-wrapper': {
      variant: 'modals.wideModalWrapper', // diff
    },
    '.modal-body': {
      variant: 'modals.commonModalBody',
    },
  })
);

const CheckoutNoResultsModal = styled(WhiteBoxModal).attrs({
  name: 'checkoutNoResultsModal',
  title: '',
})(
  sx2CssThemeFn({
    '.modal-header': {
      display: 'none',
    },
    '.modal-wrapper': {
      variant: 'modals.smallModalWrapper',
    },
    '.modal-body': {
      variant: 'modals.commonModalBody',
    },
  })
);

export const FdrWishlistCompareItemsContainer: React.FC = () => {
  const { mobile } = useDeviceType();
  const { itemsLoading } = useWishlistSelectedItemsContext();

  const { data: waitingScreenData } = useFdrWaitingScreenQuery({
    ssr: false,
    variables: {
      type: FdrWaitingScreenType.Product,
    },
  });

  const {
    onCheckout,
    isNoResultsModalShown,
    closeCheckoutResults,
    areCheckoutResultsShown,
    closeNoResultsModal,
    checkoutData,
    waitingScreenImg,
    isForceClosed,
    forceClose,
    checkoutLoading,
  } = useFdrOrlCheckout();

  const onWishlistCheckout = useCallback((item: FdrWishlistItemFragment) => {
    if (item.trackedOffer.latest) {
      onCheckout({
        offer: {
          id: item.trackedOffer.latest?.id,
          waitingScreenImage: item.trackedOffer.latest.waitingScreenImage,
          provider: item.trackedOffer.latest.provider,
        } as IFdrCheckoutOffer,
        id: item.product.id,
      })();
    }
  }, []);

  const { paymentMethods } = useFdrPageFooter();

  if (itemsLoading) return <WishlistListCompareViewContentSkeleton />;

  return (
    <AccordionStateProvider>
      <div data-id="columns">
        {mobile && (
          <FdrWishlistCompareItemsMobile onCheckout={onWishlistCheckout} />
        )}
        {!mobile && (
          <FdrWishlistCompareItemsDesktop onCheckout={onWishlistCheckout} />
        )}
      </div>
      <CheckoutNoResultsModal
        show={isNoResultsModalShown}
        onClose={closeCheckoutResults}
      >
        <FdrCheckoutNoResults onCancel={closeNoResultsModal} />
      </CheckoutNoResultsModal>
      <CheckoutResultsModal
        show={areCheckoutResultsShown}
        onClose={closeCheckoutResults}
      >
        {checkoutData?.data && (
          <FdrCheckoutResults
            {...normalizeFdrCheckoutComponent(
              checkoutData.data.fdrProductOfferCheckout
            )}
            onCancel={closeCheckoutResults}
            onAccept={() => {
              closeCheckoutResults();

              if (checkoutData.data.fdrProductOfferCheckout?.externalLink) {
                window.location.href =
                  checkoutData.data.fdrProductOfferCheckout.externalLink;
              }
            }}
          />
        )}
      </CheckoutResultsModal>
      <CheckoutWaitingScreenModal
        show={isForceClosed ? false : checkoutLoading}
        onClose={forceClose}
      >
        <FdrCheckoutWaitingScreen
          title={waitingScreenData?.fdrWaitingScreen.title || ''}
          subTitle={waitingScreenData?.fdrWaitingScreen.subTitle || ''}
          text={waitingScreenData?.fdrWaitingScreen.text || ''}
          image={{
            rawUrl: waitingScreenImg,
            alt: 'Checkout waiting screen',
            resized: [
              {
                id: '1x_desktop',
                link: {
                  type: FdrLinkType.External,
                  url: waitingScreenImg,
                },
              },
            ],
          }}
          paymentMethods={
            <FdrFooterPaymentMethods items={paymentMethods} isDesktop />
          }
        />
      </CheckoutWaitingScreenModal>
    </AccordionStateProvider>
  );
};
